import React, { useState, useEffect } from 'react';
import styles from './OptionCard.module.css';
import type { Option } from '../../types';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useLocale } from '../../contexts/LocaleContext';
import { formatPrice } from '../../utils/calculations';
import { getLocalizedString, getLocalizedStringArray, formatDaysLocalized } from '../../utils/localization';
import { renderLocalizedMarkdown } from '../../utils/markdown';
import { t } from '../../locales/translations';
import clsx from 'clsx';

interface OptionCardProps {
  option: Option;
  isSelected: boolean;
  isDisabled: boolean;
  allowMultiple: boolean;
  quantity?: number;
  onToggle: () => void;
  onQuantityChange?: (quantity: number) => void;
}

export const OptionCard: React.FC<OptionCardProps> = ({
  option,
  isSelected,
  isDisabled,
  allowMultiple,
  quantity = 1,
  onToggle,
  onQuantityChange
}) => {
  const { selectedCurrency } = useCurrency();
  const { selectedLocale } = useLocale();
  const [localQuantity, setLocalQuantity] = useState(quantity);

  useEffect(() => {
    setLocalQuantity(quantity);
  }, [quantity]);

  const pros = getLocalizedStringArray(option.pros, selectedLocale);
  const cons = getLocalizedStringArray(option.cons, selectedLocale);
  const subtasks = getLocalizedStringArray(option.subtasks, selectedLocale);

  const handleQuantityDecrease = (e: React.MouseEvent) => {
    e.stopPropagation();
    const minQuantity = option.min_instances ?? 0;
    if (localQuantity > minQuantity && onQuantityChange) {
      const newQuantity = localQuantity - 1;
      setLocalQuantity(newQuantity);
      onQuantityChange(newQuantity);
    }
  };

  const handleQuantityIncrease = (e: React.MouseEvent) => {
    e.stopPropagation();
    const maxQuantity = option.max_instances;
    if ((!maxQuantity || localQuantity < maxQuantity) && onQuantityChange) {
      const newQuantity = localQuantity + 1;
      setLocalQuantity(newQuantity);
      onQuantityChange(newQuantity);
    }
  };

  const handleQuantityInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 0) {
      const minQuantity = option.min_instances ?? 0;
      const maxQuantity = option.max_instances;
      let validQuantity = Math.max(minQuantity, value);
      if (maxQuantity) {
        validQuantity = Math.min(maxQuantity, validQuantity);
      }
      setLocalQuantity(validQuantity);
      if (onQuantityChange) {
        onQuantityChange(validQuantity);
      }
    }
  };

  const showQuantityControls = option.allow_multiple_instances;
  const totalPrice = option.price && option.price[selectedCurrency] * localQuantity;

  // For multi-instance options, selected state is based on quantity > 0
  const effectiveIsSelected = option.allow_multiple_instances ? localQuantity > 0 : isSelected;

  const handleCardClick = () => {
    if (isDisabled) return;

    if (option.allow_multiple_instances) {
      // For multiple instance options, increment the quantity
      const maxQuantity = option.max_instances;
      if (!maxQuantity || localQuantity < maxQuantity) {
        const newQuantity = localQuantity + 1;
        setLocalQuantity(newQuantity);
        if (onQuantityChange) {
          onQuantityChange(newQuantity);
        }
      }
    } else {
      // For regular options, toggle selection
      onToggle();
    }
  };

  return (
    <div
      className={clsx(
        styles.optionCard,
        effectiveIsSelected && styles.selected,
        isDisabled && styles.disabled
      )}
      onClick={handleCardClick}
    >
      <div className={styles.header}>

        {(option.ui_type === 'switch' || !option.allow_multiple_instances) && (
          <div className={styles.selectionControl}>
            {option.ui_type === 'switch' ? (
              <label
                className={styles.switchControl}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={effectiveIsSelected}
                  onChange={() => {
                    if (isDisabled) return;
                    onToggle();
                  }}
                  disabled={isDisabled}
                  className={styles.switchInput}
                />
                <span className={styles.switchSlider}></span>
              </label>
            ) : (
              <input
                type={allowMultiple ? 'checkbox' : 'radio'}
                checked={isSelected}
                onChange={() => !isDisabled && onToggle()}
                disabled={isDisabled}
                className={styles.input}
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>
        )}
        <div className={styles.titleSection}>
          <div className={styles.titleRow}>
            <h4 className={styles.title}>{getLocalizedString(option.title, selectedLocale)}</h4>
            {option.is_per_hour && (
              <span className={styles.hourlyBadge}>{t('hourly_rate', selectedLocale)}</span>
            )}
          </div>
          <div className={styles.description}>
            {renderLocalizedMarkdown(option.description, selectedLocale)}
          </div>

          {subtasks && subtasks.length > 0 && (
            <div className={styles.subtasksContainer}>
              {subtasks.map((subtask, index) => (
                <span key={index} className={styles.subtaskBadge}>
                  {subtask}
                </span>
              ))}
            </div>
          )}

          {showQuantityControls && (
            <div className={styles.quantityControls}>
              <button
                className={styles.quantityButton}
                onClick={handleQuantityDecrease}
                disabled={localQuantity <= (option.min_instances ?? 0)}
                aria-label="Decrease quantity"
              >
                −
              </button>
              <input
                type="number"
                className={styles.quantityInput}
                value={localQuantity}
                onChange={handleQuantityInputChange}
                onClick={(e) => e.stopPropagation()}
                min={option.min_instances ?? 0}
                max={option.max_instances}
                aria-label="Quantity"
              />
              <button
                className={styles.quantityButton}
                onClick={handleQuantityIncrease}
                disabled={option.max_instances ? localQuantity >= option.max_instances : false}
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
          )}
        </div>
      </div>

      {option.image && (
        <div className={styles.imageSection}>
          <img
            src={option.image.url}
            alt={getLocalizedString(option.image.title, selectedLocale)}
            className={styles.image}
          />
          <p className={styles.imageTitle}>{getLocalizedString(option.image.title, selectedLocale)}</p>
        </div>
      )}

      <div className={styles.details}>
        {pros && pros.length > 0 && (
          <div className={styles.prosConsSection}>
            <h5 className={styles.prosConsTitle}>{t('pros', selectedLocale)}</h5>
            <ul className={styles.prosList}>
              {pros.map((pro, index) => (
                <li key={index} className={styles.prosItem}>
                  <span className={styles.prosIcon}>✓</span>
                  {pro}
                </li>
              ))}
            </ul>
          </div>
        )}

        {cons && cons.length > 0 && (
          <div className={styles.prosConsSection}>
            <h5 className={styles.prosConsTitle}>{t('cons', selectedLocale)}</h5>
            <ul className={styles.consList}>
              {cons.map((con, index) => (
                <li key={index} className={styles.consItem}>
                  <span className={styles.consIcon}>×</span>
                  {con}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        {option.price && (
          <div className={styles.metric}>
            <span className={styles.metricLabel}>{t('price', selectedLocale)}</span>
            <span className={styles.price}>
              {showQuantityControls ? (
                <>
                  {formatPrice(option.price[selectedCurrency], selectedCurrency)}
                  <span className={styles.multiplication}> × {localQuantity} = </span>
                  <span className={styles.totalPrice}>
                    {formatPrice(totalPrice || 0, selectedCurrency)}
                  </span>
                  <span className={styles.vatTag}>{t('withVAT', selectedLocale)}</span>
                </>
              ) : (
                <>
                  {formatPrice(option.price[selectedCurrency], selectedCurrency)}
                  <span className={styles.vatTag}>{t('withVAT', selectedLocale)}</span>
                  {option.is_per_hour && <span className={styles.hourSuffix}>/{t('hour', selectedLocale)}</span>}
                </>
              )}
            </span>
          </div>
        )}
        {option.days_till_completed !== undefined && (
          <div className={styles.metric}>
            <span className={styles.metricLabel}>{t('timeline', selectedLocale)}</span>
            <span className={styles.timeline}>{formatDaysLocalized(option.days_till_completed, selectedLocale)}</span>
          </div>
        )}
      </div>
    </div>
  );
};